import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { getDurableConversation } from "@/api/conversations";
import { useAppLanguage } from "@/i18n";
import { usePredictedPrompt } from "@/hooks/use-predicted-prompt";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useWindowOrigin } from "@/desktop/use-window-origin";
import { conversationDisplayName } from "@/lib/conversation-display";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import type { InputTargetConversation } from "@/api/conversation-targets";
import {
  useInputBarTarget,
  type InputBarRoutingIntent,
} from "@/stores/input-bar-target-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useStandaloneComposerDraft } from "@/stores/standalone-composer-draft-store";
import type { CursorAnchor } from "./input-bar-types";
import { CURSOR_BAR_WIDTH } from "./input-bar-metrics";
import { rebaseChipOffset } from "./standalone-bar-draft";
import { standaloneBarPlaceholder } from "./standalone-bar-placeholder";
import {
  computeStandaloneBarPlacement,
  standaloneBarContainsGlobalPointer,
  standaloneBarHeightGrowth,
  STANDALONE_EXPANDED_HEIGHT,
  STANDALONE_IDLE_WIDTH,
  type StandaloneBarPlacementMode,
} from "./standalone-bar-placement";
import { standaloneBarContentWidth } from "./standalone-bar-content-width";
import { useStandaloneBarPredictionScope } from "./standalone-bar-prediction-scope";

interface Args {
  enabled: boolean;
  value: string;
  onChange: (value: string) => void;
  hasImages: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  cursorAnchor: CursorAnchor | null;
  cursorPlacement: StandaloneBarPlacementMode;
  defaultAgentName?: string;
}

/** Removing the chip puts the earlier route back as it was: a pending New
 *  chat keeps its source, frame, agent and the intent an in-flight send uses. */
function restore(snapshot: InputBarRoutingIntent | null): void {
  if (snapshot) useInputBarTarget.getState().restoreRoutingIntent(snapshot);
}

export function useStandaloneBarController({
  enabled,
  value,
  onChange,
  hasImages,
  containerRef,
  cursorAnchor,
  cursorPlacement,
  defaultAgentName,
}: Args) {
  const { draft, updateDraft } = useStandaloneComposerDraft();
  const chip = draft.chip;
  const chipRef = useRef(chip);
  chipRef.current = chip;
  const snapshotRef = useRef<InputBarRoutingIntent | null>(draft.previousRoute);
  snapshotRef.current = draft.previousRoute;
  const previousValueRef = useRef(value);
  previousValueRef.current = value;
  const [expanded, setExpanded] = useState(Boolean(value));
  const [shellHeight, setShellHeight] = useState(36);
  const [barWidth, setBarWidth] = useState(STANDALONE_IDLE_WIDTH);
  const target = useInputBarTarget((state) => state.target);
  const startNewConversation = useInputBarTarget(
    (state) => state.startNewConversation,
  );
  const conversationId = target?.conversationId;
  const conversationLabel = target?.conversationLabel;
  const subagentTarget = isSubagentTarget(target);
  const latestUserMessageId = useConversationStore((state) => {
    let latest: (typeof state.messages)[number] | undefined;
    for (const message of state.messages) {
      if (
        message.conversationId !== conversationId ||
        message.role !== "user" ||
        message.isOptimistic
      )
        continue;
      if (
        !latest ||
        message.createdAt > latest.createdAt ||
        (message.createdAt === latest.createdAt && message.id > latest.id)
      )
        latest = message;
    }
    return latest?.id;
  });
  const [latestLabel, setLatestLabel] = useState<{
    conversationId: string;
    sourceLabel: string | undefined;
    label: string;
  } | null>(null);
  useEffect(() => {
    if (!enabled || !conversationId || startNewConversation || subagentTarget)
      return;
    let active = true;
    void getDurableConversation(conversationId)
      .then((conversation) => {
        if (active && conversation.id === conversationId) {
          setLatestLabel({
            conversationId,
            sourceLabel: conversationLabel,
            label: conversationDisplayName(conversation),
          });
        }
      })
      .catch(() => {
        // Keep the selected target's existing label when offline.
      });
    return () => {
      active = false;
    };
  }, [
    enabled,
    conversationId,
    conversationLabel,
    subagentTarget,
    startNewConversation,
    latestUserMessageId,
  ]);
  const predictionScope = useStandaloneBarPredictionScope();
  // The routed chat only: never @new, never while the host's scope is off (a
  // scripted demo, a worker's question card, a blocked worker), and never a
  // chip left over for another chat than the route.
  const predictedPrompt = usePredictedPrompt(
    enabled &&
      predictionScope.enabled &&
      conversationId &&
      !startNewConversation &&
      (!chip || chip.row?.id === conversationId)
      ? conversationId
      : null,
  );
  // The placeholder is worded in the app language; re-read it on a switch.
  useAppLanguage();
  const placeholder = standaloneBarPlaceholder(
    target &&
      latestLabel &&
      latestLabel.conversationId === conversationId &&
      latestLabel.sourceLabel === conversationLabel &&
      !subagentTarget
      ? { ...target, conversationLabel: latestLabel.label }
      : target,
    startNewConversation,
    defaultAgentName,
    predictedPrompt,
  );
  const currentTargetLabel =
    target && chip?.row?.id === conversationId && !startNewConversation
      ? !subagentTarget &&
        latestLabel &&
        latestLabel.conversationId === conversationId &&
        latestLabel.sourceLabel === conversationLabel
        ? latestLabel.label
        : conversationLabel
      : null;
  const displayedChip =
    chip && currentTargetLabel
      ? { ...chip, label: `@${currentTargetLabel}` }
      : chip;

  const boundsRef = useRef<DOMRect | null>(null);
  const displayId = getWindowDisplayId();
  const windowOrigin = useWindowOrigin();
  const nativeHovered = useActiveDisplay((state) => {
    return (
      enabled &&
      standaloneBarContainsGlobalPointer(
        boundsRef.current,
        {
          x: state.cursorX,
          y: state.cursorY,
          displayId: state.activeDisplayId,
        },
        windowOrigin,
        displayId,
      )
    );
  });
  useEffect(() => {
    if (nativeHovered) setExpanded(true);
  }, [nativeHovered]);
  useEffect(() => {
    if (enabled && (value || chip || hasImages)) setExpanded(true);
  }, [enabled, value, chip, hasImages]);

  useLayoutEffect(() => {
    if (!enabled) return;
    const shell = containerRef.current?.querySelector<HTMLElement>(
      ".standalone-bar-shell",
    );
    if (!shell) return;
    const measure = () => {
      const rect = shell.getBoundingClientRect();
      boundsRef.current = rect;
      setShellHeight((current) =>
        current === rect.height ? current : rect.height,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(shell);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [containerRef, enabled]);

  const [viewportSize, setViewportSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  useEffect(() => {
    if (!enabled) return;
    const resize = () =>
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [enabled]);

  const point =
    cursorAnchor?.type === "screen"
      ? { x: cursorAnchor.localX, y: cursorAnchor.localY }
      : cursorAnchor?.type === "flow"
        ? {
            x: cursorAnchor.flowX * cursorAnchor.zoom + cursorAnchor.viewportX,
            y: cursorAnchor.flowY * cursorAnchor.zoom + cursorAnchor.viewportY,
          }
        : { x: 0, y: 0 };
  const placement =
    enabled && cursorAnchor
      ? computeStandaloneBarPlacement({
          point,
          viewport: viewportSize,
          placement: cursorPlacement,
          expandedHeight: Math.max(shellHeight, STANDALONE_EXPANDED_HEIGHT),
        })
      : null;
  const widestBar = placement
    ? Math.min(CURSOR_BAR_WIDTH, placement.maxWidth)
    : CURSOR_BAR_WIDTH;
  useEffect(() => {
    if (!enabled) return;
    setBarWidth((current) =>
      Math.max(
        current,
        standaloneBarContentWidth(containerRef.current, value, widestBar),
      ),
    );
  }, [
    enabled,
    value,
    containerRef,
    placeholder.route,
    displayedChip?.label,
    displayedChip?.offset,
    predictedPrompt,
    widestBar,
  ]);
  const width = placement ? Math.min(barWidth, placement.maxWidth) : barWidth;
  const widthGrowth = Math.max(0, width - STANDALONE_IDLE_WIDTH);
  const left = placement
    ? placement.side === "left"
      ? placement.left - widthGrowth
      : placement.side === "bottom"
        ? (viewportSize.width - width) / 2
        : placement.side === "fallback"
          ? placement.left - widthGrowth / 2
          : placement.left
    : 0;
  const heightGrowth = standaloneBarHeightGrowth(shellHeight);
  const top = placement
    ? placement.top - (placement.growsUp ? heightGrowth : 0)
    : 0;
  const originX =
    placement?.side === "left"
      ? width
      : placement?.side === "bottom"
        ? width / 2
        : (placement?.originX ?? 0);
  const originY =
    placement?.side === "bottom"
      ? shellHeight
      : (placement?.originY ?? 18) + (placement?.growsUp ? heightGrowth : 0);
  const dotLeft = Math.max(0, Math.min(originX - 3, width - 6));
  const dotTop = Math.max(0, Math.min(originY - 3, shellHeight - 6));
  const dotInsets = {
    top: dotTop,
    right: Math.max(0, width - dotLeft - 6),
    bottom: Math.max(0, shellHeight - dotTop - 6),
    left: dotLeft,
  };
  const style = placement
    ? {
        position: "absolute" as const,
        left,
        top,
        width,
        maxWidth: placement.maxWidth,
      }
    : undefined;

  function updateValue(next: string, renderedChipOffset?: number) {
    if (enabled && chipRef.current) {
      const moved = {
        ...chipRef.current,
        offset:
          renderedChipOffset ??
          rebaseChipOffset(
            previousValueRef.current,
            next,
            chipRef.current.offset,
          ),
      };
      chipRef.current = moved;
      updateDraft({ chip: moved });
    }
    previousValueRef.current = next;
    onChange(next);
  }

  function rememberRouting() {
    if (snapshotRef.current) return;
    snapshotRef.current = useInputBarTarget.getState().snapshotRoutingIntent();
    updateDraft({ previousRoute: snapshotRef.current });
  }

  function placeChip(
    label: string,
    offset: number,
    row?: InputTargetConversation | null,
  ) {
    chipRef.current = { label, offset, row };
    updateDraft({ chip: chipRef.current });
    setExpanded(true);
  }

  function restoreChipRouting() {
    if (!chipRef.current) return;
    restore(snapshotRef.current);
    snapshotRef.current = null;
    chipRef.current = null;
    updateDraft({ chip: null, previousRoute: null });
  }

  function markSent() {
    snapshotRef.current = null;
    chipRef.current = null;
    updateDraft({ chip: null, previousRoute: null });
  }

  return {
    chip: displayedChip,
    placeholder,
    expanded,
    setExpanded,
    style,
    dotInsets,
    multiline: shellHeight > 40,
    updateValue,
    rememberRouting,
    placeChip,
    restoreChipRouting,
    markSent,
  };
}
